import { Group, Mesh, type BufferGeometry, type Material, type Object3D } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export interface MergeOptions {
  castShadow?: boolean;
  receiveShadow?: boolean;
}

/**
 * Merges every single-material mesh under `source` that shares a material into one mesh.
 * Geometry is baked into `source`'s own space, so the result can replace it in place.
 */
export function mergeByMaterial(source: Object3D, options: MergeOptions = {}): Group {
  source.updateMatrixWorld(true);
  const inverseRoot = source.matrixWorld.clone().invert();
  const byMaterial = new Map<Material, BufferGeometry[]>();
  source.traverse((node) => {
    if (!(node instanceof Mesh) || Array.isArray(node.material)) return;
    const local = node.matrixWorld.clone().premultiply(inverseRoot);
    const geometry = (node.geometry as BufferGeometry).clone().applyMatrix4(local);
    const flat = geometry.index ? geometry.toNonIndexed() : geometry;
    flat.clearGroups();
    for (const name of Object.keys(flat.attributes)) {
      if (!['position', 'normal', 'uv'].includes(name)) flat.deleteAttribute(name);
    }
    const list = byMaterial.get(node.material) ?? [];
    list.push(flat);
    byMaterial.set(node.material, list);
  });
  const merged = new Group();
  for (const [material, geometries] of byMaterial) {
    const geometry = mergeGeometries(geometries);
    geometries.forEach((g) => g.dispose());
    if (!geometry) continue;
    const mesh = new Mesh(geometry, material);
    mesh.castShadow = options.castShadow ?? false;
    mesh.receiveShadow = options.receiveShadow ?? false;
    merged.add(mesh);
  }
  return merged;
}
